import Launch from '@mui/icons-material/Launch';
import PlayCircle from '@mui/icons-material/PlayCircle';
import { Box, Button, Container, Divider, Grid, List, ListItem, Typography, useMediaQuery } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Background from "../../assets/background.png";
import Bee from "../../assets/bee.png";
import BeeHives from "../../assets/beehives.png";
import SquareLogo from "../../assets/beetogether-logo-520x520.png";
import Logo from "../../assets/beetogether-logo-extended.png";
import { theme } from "../../common/config/theme";

const WEBSITE_LINK = "https://www.ita-slo.eu/bee2gether"
const VIDEO_LINK = "https://www.youtube.com/watch?v=QdSSnLoRXio&list=PLJ9xn0eU5enX3SEG9g1OuYGNU4ytNsEkA"

const ProjectPreview: FC<{}> = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const smallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const largeMediumScreen = useMediaQuery(theme.breakpoints.up("sm"));
    const [isPrivate, setIsPrivate] = useState<boolean>(false);

    useEffect(() => {
        if (location.pathname === "/project") {
            setIsPrivate(true)
        } else {
            setIsPrivate(false)
        }
    }, [location.pathname])

    return <Container sx={{ p: 2, minHeight: "calc(100vh - 198px)", position: "relative" }}>
        {
            !isPrivate && <Box>
                <Button variant="outlined" onClick={() => navigate("/login")} sx={{ position: smallScreen ? "" : "absolute", top: 16, mb: 2 }}>
                    {t("navigateBack")}
                </Button>
            </Box>
        }
        <Grid container columnSpacing={6}>
            <Grid item xs={12} md={6}>
                {
                    smallScreen && <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                        <Box component="img" src={SquareLogo} sx={{ height: 150 }}></Box>
                    </Box>
                }
                {
                    largeMediumScreen && <Box sx={{ display: "flex", justifyContent: "center", mt: !isPrivate ? 4 : 2, mb: 2 }}>
                        <Box component="img" src={Logo} sx={{ width: "100%", maxWidth: 550 }}></Box>
                    </Box>
                }
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {t("previewPage.objectiveTitle")}
                </Typography>
                <Box sx={{ mb: 2, p: 2, backgroundColor: theme.palette.secondary.light }}>
                    <Box sx={{ p: 2, backgroundColor: theme.palette.common.white }}>
                        <Typography sx={{ fontWeight: 600, textAlign: "center" }}>
                            {t("previewPage.slogan")}
                        </Typography>
                    </Box>
                </Box>
                <Typography sx={{ textAlign: "justify" }}>{t("previewPage.objectiveDescription")}</Typography>
                <Typography variant="h6" sx={{ my: 2 }}>{t("previewPage.priorityTitle")}</Typography>
                <Typography sx={{ textAlign: "justify" }}>{t("previewPage.priorityParagraph1")}</Typography>
                <Typography variant="h6" sx={{ my: 2 }}>{t("previewPage.prioritySubtitle")}</Typography>
                <Typography sx={{ textAlign: "justify" }}>{t("previewPage.priorityParagraph2")}</Typography>
                <Box sx={{
                    width: "100%",
                    height: smallScreen ? 200 : 250,
                    my: 2,
                    backgroundRepeat: "none",
                    backgroundSize: "cover",
                    backgroundImage: `url(${Background})`,
                    backgroundPosition: "center"
                }}>
                </Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {t("previewPage.summaryTitle")}
                </Typography>
                <Typography sx={{ textAlign: "justify", my: 2 }}>{t("previewPage.summaryParagraph1")}</Typography>
                <Box sx={{
                    width: "100%",
                    height: smallScreen ? 200 : 250,
                    my: 2,
                    backgroundRepeat: "none",
                    backgroundSize: "cover",
                    backgroundImage: `url(${Bee})`,
                    backgroundPosition: "center"
                }}>
                </Box>
                <Typography sx={{ textAlign: "justify", my: 2 }}>{t("previewPage.summaryParagraph2")}</Typography>
                <Box sx={{
                    width: "100%",
                    height: smallScreen ? 200 : 250,
                    my: 2,
                    backgroundRepeat: "none",
                    backgroundSize: "cover",
                    backgroundImage: `url(${BeeHives})`,
                    backgroundPosition: "center"
                }}>
                </Box>
                <Typography sx={{ textAlign: "justify", my: 2 }}>{t("previewPage.summaryParagraph3")}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ my: 2 }}>{t("previewPage.beehiveTitle")}</Typography>
                <Typography sx={{ textAlign: "justify" }}>{t("previewPage.beehiveParagraph")}</Typography>
                <Typography variant="h6" sx={{ my: 2 }}>{t("previewPage.melixaTitle")}</Typography>
                <Typography sx={{ textAlign: "justify" }}>{t("previewPage.melixaParagraph")}</Typography>
                <List sx={{ listStyleType: 'disc', pl: 2 }}>
                    <ListItem sx={{ display: 'list-item' }}>
                        <Typography sx={{ textAlign: "justify" }}>{t("previewPage.melixaElement1")}</Typography>
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                        <Typography sx={{ textAlign: "justify" }}>{t("previewPage.melixaElement2")}</Typography>
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                        <Typography sx={{ textAlign: "justify" }}>{t("previewPage.melixaElement3")}</Typography>
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                        <Typography sx={{ textAlign: "justify" }}>{t("previewPage.melixaElement4")}</Typography>
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                        <Typography sx={{ textAlign: "justify" }}>{t("previewPage.melixaElement5")}</Typography>
                    </ListItem>
                </List>
                <Typography variant="h6" sx={{ my: 2 }}>{t("previewPage.webAppTitle")}</Typography>
                <Typography sx={{ textAlign: "justify" }}>{t("previewPage.webAppParagraph")}</Typography>
                <Typography variant="h6" sx={{ my: 2 }}>{t("previewPage.mainFeaturesTitle")}</Typography>
                <List sx={{ listStyleType: 'disc', pl: 2 }}>
                    <ListItem sx={{ display: 'list-item' }}>
                        <Typography sx={{ textAlign: "justify" }}>{t("previewPage.mainFeaturesElement1")}</Typography>
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                        <Typography sx={{ textAlign: "justify" }}>{t("previewPage.mainFeaturesElement2")}</Typography>
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                        <Typography sx={{ textAlign: "justify" }}>{t("previewPage.mainFeaturesElement3")}</Typography>
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                        <Typography sx={{ textAlign: "justify" }}>{t("previewPage.mainFeaturesElement4")}</Typography>
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                        <Typography sx={{ textAlign: "justify" }}>{t("previewPage.mainFeaturesElement5")}</Typography>
                    </ListItem>
                </List>
                <Typography variant="h6" sx={{ my: 2 }}>{t("previewPage.conclusionTitle")}</Typography>
                <Typography sx={{ textAlign: "justify" }}>{t("previewPage.conclusionParagraph")}</Typography>
            </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ textAlign: "center", mb: 1 }}>
            Budget totale / Total budget / Skupno Financiranje 722,203.13€
        </Typography>
        <Typography sx={{ textAlign: "center", mb: 1 }}>
            FESR / ERDF / Prispevek ESRR 577,762.50€
        </Typography>
        <Typography sx={{ textAlign: "center", mb: 1 }}>
            Durata / Duration / Trajanje 01.09.2023 - 31.08.25
        </Typography>
        <Box sx={{ display: "flex", flexDirection: smallScreen ? "column" : "row", justifyContent: "center", my: 3 }}>
            <Button variant="outlined" component="a" sx={{ fontSize: 16, mb: smallScreen ? 2 : 0 }} target="_blank" href={WEBSITE_LINK} rel="noreferrer">
                <Launch sx={{ mr: largeMediumScreen ? 0.5 : 0 }} />
                {t("previewPage.goToWebsite")}
            </Button>
            <Button variant="outlined" component="a" sx={{ fontSize: 16, ml: smallScreen ? 0 : 1 }} target="_blank" href={VIDEO_LINK} rel="noreferrer">
                <PlayCircle sx={{ mr: largeMediumScreen ? 0.5 : 0 }} />
                {t("previewPage.goToVideo")}
            </Button>
        </Box>
    </Container>
}

export default ProjectPreview;